import React, { useEffect, useRef } from 'react';
import * as THREE from 'three';

const FRAG = /* glsl */ `
  precision highp float;

  uniform float uTime;
  uniform vec2 uRes;
  varying vec2 vUv;

  float hash(vec2 p) {
    return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453123);
  }

  float noise(vec2 p) {
    vec2 i = floor(p);
    vec2 f = fract(p);
    vec2 u = f * f * (3.0 - 2.0 * f);
    return mix(
      mix(hash(i), hash(i + vec2(1.0, 0.0)), u.x),
      mix(hash(i + vec2(0.0, 1.0)), hash(i + vec2(1.0, 1.0)), u.x),
      u.y
    );
  }

  float fbm(vec2 p) {
    float v = 0.0;
    float a = 0.5;
    for (int i = 0; i < 4; i++) {
      v += a * noise(p);
      p = p * 2.05 + vec2(13.7, 7.3);
      a *= 0.5;
    }
    return v;
  }

  void main() {
    vec2 p = (vUv - 0.5) * vec2(uRes.x / uRes.y, 1.0);
    float t = uTime * 0.045;

    // domain-warped aurora
    vec2 warp = vec2(
      fbm(p * 1.4 + vec2(t, -t * 0.7)),
      fbm(p * 1.4 + vec2(-t * 0.6, t))
    );

    float n1 = fbm(p * 1.8 + warp * 0.9 + t * 0.5);
    float n2 = fbm(p * 3.1 - warp * 0.6 - t * 0.3);

    // living palette — colors slowly drift between moods over time
    float cyc = uTime * 0.06;
    vec3 base = vec3(0.022, 0.026, 0.038);
    vec3 navy = vec3(0.10, 0.17, 0.34);
    vec3 violet = vec3(0.22, 0.11, 0.36);
    vec3 teal = vec3(0.05, 0.30, 0.32);
    vec3 ember = vec3(0.85, 0.38, 0.18);
    vec3 moss = vec3(0.10, 0.30, 0.18);

    vec3 cA = mix(navy, violet, 0.5 + 0.5 * sin(cyc));
    vec3 cB = mix(violet, teal, 0.5 + 0.5 * sin(cyc * 0.8 + 2.1));
    vec3 cC = mix(teal, moss, 0.5 + 0.5 * sin(cyc * 0.6 + 4.2));
    vec3 cD = mix(ember, violet, 0.5 + 0.5 * sin(cyc * 0.5 + 1.0));

    vec3 col = base;
    col += cA * smoothstep(0.38, 0.95, n1) * 0.58;
    col += cB * smoothstep(0.48, 1.0, n2) * 0.40;
    col += cC * smoothstep(0.62, 1.0, fbm(p * 2.4 + warp + t)) * 0.24;
    col += cD * smoothstep(0.78, 1.0, fbm(p * 4.0 - t * 1.4)) * 0.07;

    // slow breathing
    col *= 0.92 + 0.08 * sin(uTime * 0.18);

    // vignette
    col *= 1.0 - dot(p, p) * 0.5;

    gl_FragColor = vec4(col, 1.0);
  }
`;

const VERT = /* glsl */ `
  varying vec2 vUv;
  void main() {
    vUv = uv;
    gl_Position = vec4(position, 1.0);
  }
`;

export default function Background() {
  const mountRef = useRef(null);

  useEffect(() => {
    const mount = mountRef.current;
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    const renderer = new THREE.WebGLRenderer({ antialias: false, alpha: false });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 1.5));
    renderer.setSize(window.innerWidth, window.innerHeight);
    mount.appendChild(renderer.domElement);

    const scene = new THREE.Scene();
    const camera = new THREE.OrthographicCamera(-1, 1, 1, -1, 0, 1);

    const uniforms = {
      uTime: { value: 0 },
      uRes: { value: new THREE.Vector2(window.innerWidth, window.innerHeight) },
    };

    const material = new THREE.ShaderMaterial({
      uniforms,
      vertexShader: VERT,
      fragmentShader: FRAG,
      depthTest: false,
      depthWrite: false,
    });
    const geometry = new THREE.PlaneGeometry(2, 2);
    scene.add(new THREE.Mesh(geometry, material));

    const onResize = () => {
      renderer.setSize(window.innerWidth, window.innerHeight);
      uniforms.uRes.value.set(window.innerWidth, window.innerHeight);
    };
    window.addEventListener('resize', onResize);

    let raf;
    const start = performance.now();
    const tick = () => {
      raf = requestAnimationFrame(tick);
      uniforms.uTime.value = (performance.now() - start) / 1000;
      renderer.render(scene, camera);
    };

    if (reduced) {
      uniforms.uTime.value = 12;
      renderer.render(scene, camera);
    } else {
      tick();
    }

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('resize', onResize);
      geometry.dispose();
      material.dispose();
      renderer.dispose();
      mount.removeChild(renderer.domElement);
    };
  }, []);

  return <div ref={mountRef} className="bg-canvas" aria-hidden />;
}
